import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { useProfileEdit } from '../hooks/useProfileEdit';
import { Button } from '../components/ui/button';
import { LogOut } from 'lucide-react';
import axios from 'axios';

// Extracted components
import EditableName from '../components/profile/EditableName';
import EditableBio from '../components/profile/EditableBio';
import ProfilePhoto from '../components/profile/ProfilePhoto';
import ProfileInfo from '../components/profile/ProfileInfo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Profile Page - Refactored version
 * Location is now mandatory for all users
 */
const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, setUser, logout, updateProfile } = useAuth();
  const fileInputRef = useRef(null);

  // Use custom hook for all editing logic
  const {
    isEditingName,
    nameEditText,
    setNameEditText,
    handleNameClick,
    handleNameSave,
    handleNameCancel,
    isEditingBio,
    bioEditText,
    setBioEditText,
    handleBioClick,
    handleBioSave,
    handleBioCancel,
    showPhotoDialog,
    handlePhotoClick,
    handlePhotoDialogClose,
    isUploadingPhoto,
    setIsUploadingPhoto,
    isSaving
  } = useProfileEdit(user, updateProfile);

  // Photo rotation handler
  const handlePhotoRotate = async () => {
    handlePhotoDialogClose();
    
    if (!user?.photos || user.photos.length === 0) {
      toast({
        title: "No photo",
        description: "Please add a photo first before rotating",
        variant: "destructive"
      });
      return;
    }

    setIsUploadingPhoto(true);

    try {
      const currentPhotoUrl = user.photos[0];
      
      const rotateResponse = await axios.post(
        `${API_URL}/api/upload/rotate-photo`,
        { 
          photo_url: currentPhotoUrl,
          rotation: 90
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const rotatedPhotoUrl = rotateResponse.data.url;
      await updateProfile({ photos: [rotatedPhotoUrl] });

      toast({
        title: "Photo rotated!",
        description: "Your photo has been rotated 90° clockwise"
      });
    } catch (error) {
      toast({
        title: "Rotation failed",
        description: error.response?.data?.detail || "Failed to rotate photo",
        variant: "destructive"
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Photo change handler
  const handlePhotoChangeConfirm = () => {
    handlePhotoDialogClose();
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 21 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 21MB",
        variant: "destructive"
      });
      return;
    }

    setIsUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axios.post(
        `${API_URL}/api/upload/change-photo`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      const photoUrl = uploadResponse.data.url;
      setUser(prev => ({ ...prev, photos: [photoUrl] }));

      toast({
        title: "Photo updated!",
        description: "Your profile photo has been changed"
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload photo",
        variant: "destructive"
      });
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="bg-gray-50 p-4 min-h-full">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden p-6 space-y-6">
          
          {/* Profile Photo */}
          <ProfilePhoto
            user={user}
            onPhotoClick={handlePhotoClick}
            showPhotoDialog={showPhotoDialog}
            onPhotoChangeConfirm={handlePhotoChangeConfirm}
            onPhotoRotate={handlePhotoRotate}
            onPhotoDialogClose={handlePhotoDialogClose}
            isUploadingPhoto={isUploadingPhoto}
          />

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Editable Name */}
          <EditableName
            user={user}
            isEditing={isEditingName}
            nameEditText={nameEditText}
            onNameChange={setNameEditText}
            onNameSave={handleNameSave}
            onNameCancel={handleNameCancel}
            onNameClick={handleNameClick}
            isSaving={isSaving}
          />

          {/* Profile Info (username, email, age, location) */}
          <ProfileInfo user={user} />

          <div className="pt-6 space-y-6">
            {/* Editable Bio */}
            <EditableBio
              user={user}
              isEditing={isEditingBio}
              bioEditText={bioEditText}
              onBioChange={setBioEditText}
              onBioSave={handleBioSave}
              onBioCancel={handleBioCancel}
              onBioClick={handleBioClick}
              isSaving={isSaving}
            />
          </div>

          {/* Logout Button - At Bottom */}
          <div className="pt-6">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-red-200 text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
